<template>
    <div class="container-fluid h-100 my-2">
        <div class="row justify-content-center align-items-center h-100">
            <div class="col-auto">
                <div class="box loading_screen" id="login">
                    <groomy-logo />
                    <ol class="loading_steps mt-4" aria-live="polite">
                        <li v-for="step in steps" :key="step.code" class="loading_step" :class="'loading_step--' + step.status">
                            <span class="loading_step_icon">
                                <font-awesome-icon v-if="step.status == 'running'" :icon="['fas', 'spinner']" pulse />
                                <font-awesome-icon v-else-if="step.status == 'done'" :icon="['far', 'check']" />
                                <font-awesome-icon v-else-if="step.status == 'warning'" :icon="['fas', 'exclamation-triangle']" />
                                <font-awesome-icon v-else-if="step.status == 'error'" :icon="['far', 'times']" />
                                <font-awesome-icon v-else-if="step.status == 'skipped'" :icon="['fas', 'wifi-slash']" />
                            </span>
                            <span class="loading_step_label">{{ stepLabel(step) }}</span>
                            <span class="loading_step_time">{{ stepTime(step) }}</span>

                            <template v-if="step.status == 'running' || step.status == 'warning'">
                                <div v-if="step.code == 'database' && step.sub" class="loading_step_detail">
                                    {{ $t('loading_screen.database.' + step.sub) }}
                                </div>
                                <template v-if="step.code == 'sync'">
                                    <div v-if="progressSynchroText" class="loading_step_detail">{{ progressSynchroText }}</div>
                                    <div class="alert alert-info loading_step_alert">{{ $t('loading_screen.sync_info') }}</div>
                                </template>
                            </template>

                            <div v-if="step.status == 'warning'" class="alert alert-warning loading_step_alert">
                                <template v-if="step.warning == 'server'">
                                    {{ $t('loading_screen.warning_server') }}
                                    <template v-if="offlineAvailable">{{ $t('loading_screen.warning_server_offline') }}</template>
                                </template>
                                <template v-else>{{ $t('loading_screen.warning_' + step.warning) }}</template>
                                <div class="loading_step_actions">
                                    <b-button v-if="step.warning == 'server' && offlineAvailable" size="sm" variant="primary" @click="goOffline">
                                        {{ $t('loading_screen.go_offline') }}
                                    </b-button>
                                    <b-button size="sm" variant="secondary" @click="retry">{{ $t('loading_screen.retry') }}</b-button>
                                </div>
                            </div>

                            <div v-if="step.status == 'error'" class="alert alert-danger loading_step_alert">
                                <template v-if="step.warning == 'crash'">{{ $t('loading_screen.warning_crash') }}</template>
                                <template v-else>{{ $t('loading_screen.error', { error: step.error }) }}</template>
                                <div class="loading_step_actions">
                                    <b-button size="sm" variant="secondary" @click="retry">{{ $t('loading_screen.retry') }}</b-button>
                                </div>
                            </div>
                        </li>
                    </ol>

                    <div class="loading_details">
                        <b-button variant="link" size="sm" @click="showDetails = !showDetails">
                            {{ showDetails ? $t('loading_screen.hide_details') : $t('loading_screen.show_details') }}
                        </b-button>
                        <template v-if="showDetails">
                            <pre ref="diagnostic" class="loading_diagnostic">{{ diagnostic }}</pre>
                            <b-button size="sm" variant="secondary" @click="copyDiagnostic">
                                <font-awesome-icon :icon="['far', 'copy']" /> {{ $t('loading_screen.copy_details') }}
                            </b-button>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Constants from 'Constants'
import { EventBus } from 'EventBus'
import User from '@/mixins/User'
import Navigation from '@/mixins/Navigation.js'
import GroomyConfig from '@/assets/js/dexie/GroomyConfig'
import _uniq from 'lodash/uniq'

// Au-delà, une étape est signalée comme lente, sans jamais être interrompue
const SLOW_STEP_DELAY = 10000
// Au-delà, une base locale qui ne s'ouvre toujours pas, sans autre onglet qui la retienne, a vraisemblablement planté
const DATABASE_CRASH_DELAY = 30000
// Erreurs d'une base IndexedDB plantée (« Internal error opening backing store » sous Chrome,
// « Connection to Indexed Database server lost » sous Safari) : seul un redémarrage complet du navigateur la relance
const DATABASE_CRASH_ERRORS = ['UnknownError', 'DatabaseClosedError', 'InvalidStateError']

export default {
    name: 'LoadingScreen',
    mixins: [User, Navigation],
    props: {
        fetchRequired: {
            type: Boolean,
            default: () => (false)
        }
    },
    // steps[].status : pending | running | warning | error | done | skipped
    // steps[].warning : server | blocked | slow | crash
    // steps[].phaseStartedAt : début de la sous-étape en cours, base du calcul de lenteur
    // needFullSync : tant qu'on ne sait pas, on considère qu'une synchro complète est nécessaire, donc pas de mode hors ligne
    data: () => ({
        steps: ['session', 'database', 'sync', 'config', 'access'].map(code => ({
            code,
            status: 'pending',
            startedAt: null,
            phaseStartedAt: null,
            duration: null,
            sub: null,
            warning: null,
            error: null
        })),
        now: Date.now(),
        startedAt: new Date(),
        ticker: null,
        needFullSync: true,
        lastRequiredSync: 0,
        offlineChosen: false,
        storageEstimate: null,
        showDetails: false,
        reported: []
    }),
    computed: {
		synchroModelsWorking() {
			return this.$store.state.sync.progressModels
		},
		progressSynchroText() {
			if(this.synchroModelsWorking.length === 0) return ''
			const modules_trad = this.synchroModelsWorking.map(model => {
				// Ne pas afficher les tables pas traduites
				const tradCode = 'progression_synchro.'+model
				const trad = this.getTrad(tradCode)
				return trad === tradCode ? '' : trad
			})
			.filter((trad) => (trad))

			return this.getTrad('progression_synchro.generic') + ' ' + _uniq(modules_trad).join(', ')
		},
        currentStep() {
            return this.steps.find(step => ['running', 'warning', 'error'].includes(step.status))
        },
        // Les données locales suffisent pour travailler : inutile d'attendre un serveur qui ne répond pas
        offlineAvailable() {
            return !this.needFullSync && !this.offlineChosen
        },
        // Résumé texte du démarrage, copié pour le support et joint aux signalements Rollbar
        diagnostic() {
            const lines = [
                `${this.startedAt.toLocaleString()} · ${process.env.VUE_APP_VERSION} (${process.env.VUE_APP_BUILD_VERSION}) · ${Constants.USER_LICENCE_KEY}`
            ]

            this.steps.forEach(step => {
                let state = step.status
                const elapsed = this.stepElapsed(step)
                if (elapsed !== null) state += ' ' + this.formatDuration(elapsed)
                if (step.sub) state += ' · ' + step.sub
                if (step.warning) state += ' · ' + step.warning
                if (step.error) state += ' · ' + step.error
                lines.push(`${this.$t('loading_screen.steps.' + step.code)} : ${state}`)
            })

            lines.push(`full_sync: ${this.needFullSync} · last_required_sync: ${this.lastRequiredSync ? new Date(this.lastRequiredSync * 1000).toLocaleString() : 'none'} · offline: ${this.offlineChosen}`)
            lines.push(`online: ${window.navigator.onLine} · discarded: ${document.wasDiscarded === true}`)
            if (this.storageEstimate) {
                lines.push(`storage: ${Math.round(this.storageEstimate.usage / 1048576)} Mo / ${Math.round(this.storageEstimate.quota / 1048576)} Mo`)
            }
            lines.push(window.navigator.userAgent)

            return lines.join('\n')
        }
    },
    created() {
        this.init()
    },
    beforeDestroy() {
        clearInterval(this.ticker)
        EventBus.$off('Loading::step', this.onDatabaseStep)
        EventBus.$off('Loading::blocked', this.onDatabaseBlocked)
    },
    methods: {
        async init() {
            // Se produit quand une URL loggedIn est demandée alors que le user n'est pas connecté
            if (!this.checkLocal()) {
                return
			}

            this.ticker = setInterval(this.onTick, 500)
            this.offlineChoice = new Promise(resolve => { this.resolveOfflineChoice = resolve })
            EventBus.$on('Loading::step', this.onDatabaseStep)
            EventBus.$on('Loading::blocked', this.onDatabaseBlocked)

            try {
                await this.runStep('session', this.initLocalConfig)
                // Il doit etre fait avant n'importe quelle requete
                // pouvant renvoyer une 401 pour token expiré
                await this.runStep('database', this.initStorage)
                await this.runStep('sync', this.initSync)
                await this.runStep('config', this.initRemoteConfig)
                await this.runStep('access', this.initAccess)

                this.redirect()
            }
            catch(e) {
                console.error(e)
                // Une erreur de redirect() survient une fois toutes les étapes terminées : on la rattache à la dernière
                const step = this.currentStep || this.steps[this.steps.length - 1]
                step.status = 'error'
                // Certaines requêtes rejettent la réponse axios brute plutôt qu'une Error
                step.error = e.message || (e.data && e.data.message) || String(e)
                // Dexie enveloppe l'erreur IndexedDB d'origine dans inner (OpenFailedError)
                const errorNames = [e.name, e.inner && e.inner.name]
                step.warning = errorNames.some(name => DATABASE_CRASH_ERRORS.includes(name)) ? 'crash' : null
                this.report('error', step)
            }
        },

        getStep(code) {
            return this.steps.find(step => step.code === code)
        },

        /**
         * Exécute une étape du démarrage en suivant son état et sa durée
         * Aucune étape n'est interrompue : sa lenteur est signalée par onTick()
         * @param {String} code Code de l'étape
         * @param {Function} fn Traitement de l'étape, renvoie true si l'étape a été sautée (mode hors ligne)
         */
        async runStep(code, fn) {
            const step = this.getStep(code)
            step.status = 'running'
            step.startedAt = step.phaseStartedAt = Date.now()

            const skipped = await fn()

            step.duration = Date.now() - step.startedAt
            step.status = skipped === true ? 'skipped' : 'done'
            step.sub = null
            step.warning = null
        },

        // Rafraîchit les durées affichées et signale l'étape en cours comme lente au-delà de SLOW_STEP_DELAY,
        // puis la base locale comme plantée si son ouverture dépasse DATABASE_CRASH_DELAY
        onTick() {
            this.now = Date.now()

            const step = this.currentStep
            // La synchro complète n'a pas de seuil : les modules qui défilent montrent qu'elle avance
            if (!step || step.code === 'sync' || !['running', 'warning'].includes(step.status)) {
                return
            }

            const elapsed = this.now - step.phaseStartedAt
            if (step.code === 'database' && step.sub === 'open' && !['blocked', 'crash'].includes(step.warning) && elapsed >= DATABASE_CRASH_DELAY) {
                step.status = 'warning'
                step.warning = 'crash'
                this.report('warning', step)
            }
            else if (step.status === 'running' && elapsed >= SLOW_STEP_DELAY) {
                step.status = 'warning'
                step.warning = step.code === 'config' || (step.code === 'database' && step.sub === 'schema') ? 'server' : 'slow'
                this.report('warning', step)
            }
        },

        // Sous-étape de l'ouverture de la base, émise par GroomyDB et le plugin Storage
        onDatabaseStep(sub) {
            const step = this.getStep('database')
            step.sub = sub
            step.phaseStartedAt = Date.now()
            step.status = 'running'
            step.warning = null
        },

        onDatabaseBlocked() {
            const step = this.getStep('database')
            step.status = 'warning'
            step.warning = 'blocked'
            this.report('warning', step)
        },

        goOffline() {
            this.offlineChosen = true
            // GroomyDB cesse d'attendre le schéma serveur, initRemoteConfig() la configuration
            EventBus.$emit('Loading::offline')
            this.resolveOfflineChoice()

            const step = this.currentStep
            step.status = 'running'
            step.warning = null
            step.phaseStartedAt = Date.now()
            this.report('info', step)
        },

        retry() {
            window.location.reload()
        },

        /**
         * Envoie l'état du démarrage à Rollbar, une seule fois par étape et par type de signalement
         * On appelle $rollbar directement : le relais d'App.vue filtre sur debug_level, pas encore chargé au premier démarrage
         * @param {String} level info | warning | error
         * @param {Object} step Étape concernée
         */
        report(level, step) {
            const message = `LoadingScreen::${step.code}::${step.warning || level}`
            // Un crash peut être signalé deux fois : en avertissement (ouverture sans réponse), puis en erreur
            if (this.reported.includes(level + message)) {
                return
            }
            this.reported.push(level + message)

            if (this.$rollbar) {
                this.$rollbar[level](message, { diagnostic: this.diagnostic })
            }
        },

        copyDiagnostic() {
            navigator.clipboard.writeText(this.diagnostic)
                .then(() => this.successToast('loading_screen.details_copied'))
                .catch(() => {
                    // Presse-papiers refusé (contexte non sécurisé, webview) : on sélectionne le texte pour une copie manuelle
                    const range = document.createRange()
                    range.selectNodeContents(this.$refs.diagnostic)
                    window.getSelection().removeAllRanges()
                    window.getSelection().addRange(range)
                })
        },

        /**
         * Libellé d'une étape, adapté au mode hors ligne et à la synchro déjà à jour
         * @param {Object} step Étape affichée
         */
        stepLabel(step) {
            if (step.status === 'skipped') {
                return this.$t('loading_screen.offline_steps.' + step.code)
            }
            if (step.code === 'sync' && step.status === 'done' && !this.needFullSync) {
                return this.$t('loading_screen.steps.sync_up_to_date')
            }
            return this.$t('loading_screen.steps.' + step.code)
        },

        /**
         * Durée d'une étape : définitive si elle est terminée, écoulée si elle est en cours
         * @param {Object} step Étape
         * @return {Number|null} Durée en millisecondes, null si l'étape n'a pas commencé
         */
        stepElapsed(step) {
            if (step.duration !== null) return step.duration
            return step.startedAt ? this.now - step.startedAt : null
        },

        stepTime(step) {
            const elapsed = this.stepElapsed(step)
            return elapsed >= 1000 ? this.formatDuration(elapsed) : ''
        },

        /**
         * Formate une durée pour l'affichage : « 42 s » ou « 2 min 05 s »
         * @param {Number} ms Durée en millisecondes
         */
        formatDuration(ms) {
            const seconds = Math.floor(ms / 1000)
            return seconds < 60 ? `${seconds} s` : `${Math.floor(seconds / 60)} min ${String(seconds % 60).padStart(2, '0')} s`
        },

        async initLocalConfig() {
			// Migration de l'ancienne config vers la nouvelle
			// TODO: A supprimer lorsque tout le monde sera passé à GroomyConfig
			await GroomyConfig.getInstance()
			// Setup licence, bearer et langue en local storage
			await this.initSession()
		},
        /**
         * Ouvre la base locale et le module de synchro
         * Détermine au préalable si une synchro complète sera nécessaire : pas de base, ou synchro required non terminée
         */
        async initStorage() {
			this.storageEstimate = await this.$storage.getEstimatedQuota()
			const dbExists = await this.$storage.dbExists()
			this.lastRequiredSync = this.$sync.getLastSync('required')
			this.needFullSync = !dbExists || this.lastRequiredSync == 0

			await this.$storage.init()
			await this.$sync.init()
        },
        /**
         * Lance la synchro complète si nécessaire
         * @return {Boolean} true si l'étape est sautée parce que l'utilisateur a choisi le mode hors ligne
         */
        async initSync() {
			if (this.offlineChosen) {
				return true
			}
			if (this.needFullSync) {
				await this.$sync.force(false)
			}
        },
        /**
         * Récupère la configuration du compte depuis le serveur
         * Si l'utilisateur passe hors ligne, l'attente est abandonnée et la configuration enregistrée sur l'appareil est conservée
         * @return {Boolean} true si l'étape est sautée (mode hors ligne)
         */
		async initRemoteConfig() {
			// Setup config localstorage global
			if (!this.offlineChosen) {
				await Promise.race([this.persistUserConfig(), this.offlineChoice])
			}

			if (this.offlineChosen) {
				await this.initUserConfig()
				return true
			}
		},
        async initAccess() {
            this.initBrowserPermissions()
            this.initNotifications()
            this.initNativComm()
            await this.initUserPermissions()
        },
        initBrowserPermissions() {
            this.$storage.isStoragePersisted().then(isPersisted => {
                if (!isPersisted) {
                    this.$storage.persist()
                }
            })

            if ('Notification' in window) {
                window.Notification.requestPermission()
            }
        },
        initNotifications() {
            this.$firebase.messaging.init()
        },
        initNativComm() {
            this.$nativeComm.init()
        },
        redirect() {
            if (this.$route.query.redirect) {
                this.$router.replace(this.$route.query.redirect)
            } else {
                this.$router.replace({ name: 'cards'})
            }
        }
    }
}
</script>
